import { DEFAULT_MESSAGE_BEHAVIOR, type MessageBehavior } from './base';
import { MESSAGE_TYPE_MODULES } from './modules';

/**
 * Per-type inbox behavior, keyed by discriminator.
 *
 * Deliberately separate from `catalog.ts`: the inbox handlers need a type's
 * behavior but none of the read-path schemas, and `catalog.ts` folds the
 * chat-bubble stores in to build those. Keeping this fold store-free is what
 * lets `useTwilio` depend on a message type's behavior without loading the
 * bubble stores behind it.
 *
 * The key is read off each module's own `type` literal rather than restated
 * here, so a module and its behavior entry cannot drift apart. A module that
 * exports no `behavior` gets the default — declaring one is opt-in.
 */
const behaviorByType = new Map<string, MessageBehavior>(
  MESSAGE_TYPE_MODULES.map((module) => {
    const discriminator = module.payloadSchema.shape.type.value;

    const overrides = 'behavior' in module ? module.behavior : undefined;

    return [discriminator, { ...DEFAULT_MESSAGE_BEHAVIOR, ...overrides }];
  })
);

/**
 * Resolves a message type's behavior. An unknown or absent type falls back to
 * the default, which keeps a message the app doesn't recognize behaving like
 * an ordinary one rather than silently vanishing from the inbox.
 */
export function getMessageBehavior(type: string | undefined): MessageBehavior {
  if (!type) {
    return DEFAULT_MESSAGE_BEHAVIOR;
  }

  return behaviorByType.get(type) ?? DEFAULT_MESSAGE_BEHAVIOR;
}
